import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  LinearProgress,
  Alert,
  AlertTitle,
  Chip,
  IconButton,
  Paper,
  Tooltip,
  CircularProgress
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import { API_ENDPOINTS } from '../../../../Api/endpoints';
import { addAuthHeader } from '../../../../utils/authUtils';

const steps = ['Select Document Type', 'Upload File', 'Review & Confirm'];

// Extraction helpers (future feature — kept inline for UI compatibility)
const formatConfidence = (c) => c != null ? `${Math.round(c * 100)}%` : '';
const getConfidenceColor = (level) => {
  if (level === 'HIGH') return 'success';
  if (level === 'MEDIUM') return 'warning';
  return 'default';
};
const simulateExtraction = () => Promise.resolve(null);

/**
 * File Upload Zone Component
 * Supports multiple file selection and drag & drop
 */
const FileUploadZone = ({ files, onFilesSelect, onRemoveFile }) => {
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFileChange = (event) => {
    const selected = Array.from(event.target.files || []);
    if (selected.length > 0) {
      onFilesSelect(selected);
    }
    // Reset input so same files can be re-selected
    event.target.value = '';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const dropped = Array.from(e.dataTransfer.files || []);
    if (dropped.length > 0) {
      onFilesSelect(dropped);
    }
  };

  const handleClick = () => {
    inputRef.current?.click();
  };

  return (
    <Box>
      {/* Selected files list */}
      {files.length > 0 && (
        <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {files.map((file, index) => (
            <Paper
              key={`${file.name}-${index}`}
              variant="outlined"
              sx={{
                p: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                bgcolor: 'success.lighter'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CheckCircleIcon color="success" />
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    {file.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </Typography>
                </Box>
              </Box>
              <IconButton size="small" onClick={() => onRemoveFile(index)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Paper>
          ))}
        </Box>
      )}

      {/* Drop zone — always visible to allow adding more files */}
      <Paper
        variant="outlined"
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          p: 4,
          textAlign: 'center',
          cursor: 'pointer',
          bgcolor: isDragging ? 'action.hover' : 'background.default',
          borderStyle: 'dashed',
          borderColor: isDragging ? 'primary.main' : 'divider',
          transition: 'all 0.2s',
          '&:hover': {
            borderColor: 'primary.main',
            bgcolor: 'action.hover'
          }
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          multiple
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
        <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
        <Typography variant="body1" color="text.secondary" gutterBottom>
          {isDragging ? 'Drop files here...' : files.length > 0 ? 'Add more files' : 'Drag & drop files here, or click to select'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Supported formats: PDF, JPG, PNG (max 10MB each)
        </Typography>
      </Paper>
    </Box>
  );
};

/**
 * Extraction Status Component
 */
const ExtractionStatus = ({ status, progress, extractionResult }) => {
  if (status === 'idle') return null;

  if (status === 'extracting') {
    return (
      <Box sx={{ mt: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <AutoFixHighIcon color="primary" />
          <Typography variant="body2" fontWeight="medium">
            AI Extraction in Progress...
          </Typography>
        </Box>
        <LinearProgress variant="determinate" value={progress} sx={{ borderRadius: 1 }} />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          Analyzing document and extracting information
        </Typography>
      </Box>
    );
  }

  if (status === 'completed' && extractionResult) {
    return (
      <Alert severity="success" sx={{ mt: 2 }}>
        <AlertTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          Extraction Complete
          <Chip
            label={`${formatConfidence(extractionResult.overallConfidence)} confidence`}
            size="small"
            color="success"
          />
        </AlertTitle>
        {extractionResult.fields.length} fields extracted. Please review and edit if needed.
        {extractionResult.warnings?.length > 0 && (
          <Box sx={{ mt: 1 }}>
            {extractionResult.warnings.map((warning, index) => (
              <Typography key={index} variant="caption" display="block" color="warning.main">
                {warning.message}
              </Typography>
            ))}
          </Box>
        )}
      </Alert>
    );
  }

  if (status === 'failed') {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        <AlertTitle>Extraction Failed</AlertTitle>
        Unable to extract information automatically. Please enter details manually.
      </Alert>
    );
  }

  return null;
};

/**
 * Dynamic Field Renderer
 */
const DynamicFieldRenderer = ({ field, value, onChange, extractedData }) => {
  const extractedField = extractedData?.fields?.find(f => f.fieldCode === field.fieldCode);
  const hasExtractedValue = extractedField && extractedField.extractedValue;

  const renderField = () => {
    switch (field.fieldType) {
      case 'SELECT':
        return (
          <TextField
            select
            label={field.fieldName}
            value={value || ''}
            onChange={(e) => onChange(field.fieldCode, e.target.value)}
            fullWidth
            required={field.isRequired}
            size="small"
          >
            {(field.options || []).map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        );
      case 'DATE':
        return (
          <TextField
            type="date"
            label={field.fieldName}
            value={value || ''}
            onChange={(e) => onChange(field.fieldCode, e.target.value)}
            fullWidth
            required={field.isRequired}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
        );
      case 'NUMBER':
        return (
          <TextField
            type="number"
            label={field.fieldName}
            value={value || ''}
            onChange={(e) => onChange(field.fieldCode, e.target.value)}
            fullWidth
            required={field.isRequired}
            size="small"
          />
        );
      case 'TEXT':
      default:
        return (
          <TextField
            label={field.fieldName}
            value={value || ''}
            onChange={(e) => onChange(field.fieldCode, e.target.value)}
            fullWidth
            required={field.isRequired}
            size="small"
            multiline={field.fieldName.toLowerCase().includes('address')}
            rows={field.fieldName.toLowerCase().includes('address') ? 2 : 1}
          />
        );
    }
  };

  return (
    <Box sx={{ position: 'relative' }}>
      {renderField()}
      {hasExtractedValue && (
        <Tooltip
          title={`AI extracted with ${formatConfidence(extractedField.confidence)} confidence`}
        >
          <Chip
            icon={<AutoFixHighIcon />}
            label="AI"
            size="small"
            color={getConfidenceColor(extractedField.confidenceLevel)}
            sx={{
              position: 'absolute',
              right: 8,
              top: -8,
              height: 20,
              fontSize: '0.65rem'
            }}
          />
        </Tooltip>
      )}
    </Box>
  );
};

/**
 * Add Document Dialog
 * Multi-step dialog for uploading organization documents
 *
 * Steps:
 * 1. Select document type
 * 2. Upload file (with AI extraction)
 * 3. Review and confirm fields
 *
 * @mock Uses mock data and simulated extraction
 */
/**
 * Build partial org profile update from uploaded document metadata.
 * Maps document type code to org profile fields for auto-syncing.
 */
const buildOrgProfileUpdate = (docTypeCode, uploadedDocs, sharedMetadata) => {
  const update = {};
  const primary = uploadedDocs[uploadedDocs.length - 1]; // last uploaded = primary

  // Map document type code to the corresponding org profile document field
  const docPathMap = {
    'TRADE_LICENSE': 'tradeLicenseCopy',
    'MEMO_OF_ASSOCIATION': 'memoOfAssociation',
    'ARTICLE_OF_ASSOCIATION': 'articleOfAssociation',
    'ESTABLISHMENT_CARD': 'establishmentCardCopy',
    'IMMIGRATION_CARD': 'immigrationCardCopy',
    'CHAMBER_CERTIFICATE': 'chamberCertificateCopy',
    'VAT_CERTIFICATE': 'vatCertificateCopy',
    'TRN_CERTIFICATE': 'trnCertificateCopy',
    'SHARE_CAPITAL_CERTIFICATE': 'shareCapitalCertificate',
    'POWER_OF_ATTORNEY': 'powerOfAttorney',
    'BOARD_RESOLUTION': 'boardResolution',
    'AUDITED_FINANCIALS': 'auditedFinancials',
  };
  if (docPathMap[docTypeCode] && primary?.filePath) {
    update[docPathMap[docTypeCode]] = primary.filePath;
  }

  // Map document metadata to the corresponding org profile text/date fields
  const metadataMap = {
    'TRADE_LICENSE': { number: 'licenseNumber', issue: 'licenseIssueDate', expiry: 'licenseExpiryDate' },
    'ESTABLISHMENT_CARD': { number: 'establishmentCardNumber', expiry: 'establishmentCardExpiry' },
    'IMMIGRATION_CARD': { number: 'immigrationCardNumber', expiry: 'immigrationCardExpiry' },
    'TRN_CERTIFICATE': { number: 'trnNumber' },
    'VAT_CERTIFICATE': { number: 'vatRegistrationNumber', issue: 'vatRegistrationDate' },
    'CHAMBER_CERTIFICATE': { number: 'chamberMembershipNumber' },
    'MOHRE_LICENSE': { number: 'mohreLicenseNumber' },
  };
  const meta = metadataMap[docTypeCode];
  if (meta && sharedMetadata) {
    if (meta.number && sharedMetadata.documentNumber) update[meta.number] = sharedMetadata.documentNumber;
    if (meta.issue && sharedMetadata.issueDate) update[meta.issue] = sharedMetadata.issueDate;
    if (meta.expiry && sharedMetadata.expiryDate) update[meta.expiry] = sharedMetadata.expiryDate;
  }

  return Object.keys(update).length ? update : null;
};

const AddDocumentDialog = ({
  open,
  onClose,
  onSave,
  preSelectedCategory,
  organizationId,
  businessCategoryCode,
  businessCategory
}) => {
  // State
  const [activeStep, setActiveStep] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState(preSelectedCategory || '');
  const [selectedDocType, setSelectedDocType] = useState('');
  const [selectedAuthority, setSelectedAuthority] = useState('');
  const [files, setFiles] = useState([]);
  const [extractionStatus, setExtractionStatus] = useState('idle'); // idle, extracting, completed, failed
  const [extractionProgress, setExtractionProgress] = useState(0);
  const [extractionResult, setExtractionResult] = useState(null);
  const [fieldValues, setFieldValues] = useState({});
  const [issueDate, setIssueDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [documentNumber, setDocumentNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // API-fetched data
  const [categories, setCategories] = useState([]);
  const [allDocumentTypes, setAllDocumentTypes] = useState([]);

  // Fetch categories on mount
  useEffect(() => {
    if (!open) return;
    const fetchCategories = async () => {
      try {
        const res = await axios.get(API_ENDPOINTS.ORGANIZATION_DOCUMENT_CATEGORIES.LIST, {
          headers: addAuthHeader()
        });
        setCategories((res.data || []).filter(c => c.isActive !== false));
      } catch (err) {
        console.error('Failed to fetch categories:', err);
      }
    };
    fetchCategories();
  }, [open]);

  // Fetch document types when category changes
  useEffect(() => {
    if (!selectedCategory) {
      setAllDocumentTypes([]);
      return;
    }
    const fetchTypes = async () => {
      try {
        const res = await axios.get(
          API_ENDPOINTS.ORGANIZATION_DOCUMENT_TYPES.BY_CATEGORY(selectedCategory),
          { headers: addAuthHeader() }
        );
        setAllDocumentTypes((res.data || []).filter(t => t.isActive !== false));
      } catch (err) {
        console.error('Failed to fetch document types:', err);
        setAllDocumentTypes([]);
      }
    };
    fetchTypes();
  }, [selectedCategory]);

  const selectedDocTypeObj = allDocumentTypes.find(dt => dt.id === selectedDocType);
  // Fields and authorities: future feature — empty for now
  const fields = [];
  const authorities = [];
  // Business category features: not yet backed by API
  const requiredDocsForCategory = [];

  // Reset when dialog opens
  useEffect(() => {
    if (open) {
      setActiveStep(0);
      setSelectedCategory(preSelectedCategory || '');
      setSelectedDocType('');
      setSelectedAuthority('');
      setFiles([]);
      setExtractionStatus('idle');
      setExtractionProgress(0);
      setExtractionResult(null);
      setFieldValues({});
      setIssueDate('');
      setExpiryDate('');
      setDocumentNumber('');
      setNotes('');
    }
  }, [open, preSelectedCategory]);

  // Handle category change
  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
    setSelectedDocType('');
    setSelectedAuthority('');
  };

  // Handle doc type change
  const handleDocTypeChange = (e) => {
    setSelectedDocType(e.target.value);
  };

  // Handle files select (append to existing)
  const handleFilesSelect = (newFiles) => {
    setFiles(prev => [...prev, ...newFiles]);
  };

  // Handle single file remove by index
  const handleRemoveFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Handle field change
  const handleFieldChange = (fieldCode, value) => {
    setFieldValues(prev => ({ ...prev, [fieldCode]: value }));
  };

  // Handle next step
  const handleNext = () => {
    setActiveStep(prev => prev + 1);
  };

  // Handle back step
  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  // Check if step is valid
  const isStepValid = (step) => {
    switch (step) {
      case 0:
        return selectedCategory && selectedDocType;
      case 1:
        return files.length > 0;
      case 2:
        // Check required fields
        const requiredFields = fields.filter(f => f.isRequired);
        return requiredFields.every(f => fieldValues[f.fieldCode]);
      default:
        return false;
    }
  };

  // Handle save — upload all files then call bulk save API
  const handleSave = async () => {
    setSaving(true);

    try {
      // 1. Upload each file to get filePaths
      const uploadedDocs = await Promise.all(files.map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('fileType', 'ORGANISATION_DOCUMENT');
        formData.append('fileName', file.name);

        const uploadRes = await axios.post(API_ENDPOINTS.FILES.UPLOAD, formData, {
          headers: {
            ...addAuthHeader(),
            'Content-Type': 'multipart/form-data'
          }
        });

        const filePath = uploadRes.data.data?.filePath || uploadRes.data.filePath;

        return {
          filePath,
          fileName: file.name,
          contentType: file.type,
          fileSize: file.size,
        };
      }));

      // 2. Call bulk save API with all documents
      const bulkRequest = {
        documentTypeId: selectedDocType,
        replaceExisting: false,
        sharedMetadata: {
          issueDate: issueDate || null,
          expiryDate: expiryDate || null,
          documentNumber: documentNumber || null,
        },
        documents: uploadedDocs,
        notes: notes || null,
      };

      const bulkResponse = await axios.post(
        API_ENDPOINTS.ORGANIZATION_DOCUMENTS.BULK(organizationId),
        bulkRequest,
        { headers: addAuthHeader() }
      );

      // Step 2: Update org profile with document metadata (same pattern as EmployeeDetail)
      try {
        const docTypeCode = selectedDocTypeObj?.code || '';
        const profileUpdate = buildOrgProfileUpdate(docTypeCode, uploadedDocs, {
          issueDate: issueDate || null,
          expiryDate: expiryDate || null,
          documentNumber: documentNumber || null,
        });

        if (profileUpdate && organizationId) {
          await axios.patch(
            API_ENDPOINTS.ORG_DETAILS.BY_ORG(organizationId),
            profileUpdate,
            { headers: addAuthHeader() }
          );
          console.log('Updated org profile from document upload:', profileUpdate);
        }
      } catch (profileErr) {
        console.warn('Failed to update org profile after document upload:', profileErr);
        // Don't fail the whole save if profile update fails
      }

      onSave();
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  // Render step content
  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={2}>
            {/* Business Category Info Banner */}
            {businessCategory && (
              <Grid item xs={12}>
                <Alert
                  severity="info"
                  icon={false}
                  sx={{
                    backgroundColor: `${businessCategory.color}15`,
                    borderLeft: `4px solid ${businessCategory.color}`
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" fontWeight="medium">
                      Your Business Category: {businessCategory.name}
                    </Typography>
                    <Chip
                      label={`${requiredDocsForCategory.filter(d => d.isMandatory).length} required docs`}
                      size="small"
                      sx={{ bgcolor: businessCategory.color, color: 'white' }}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Industry-specific documents are highlighted below
                  </Typography>
                </Alert>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                select
                label="Document Category"
                value={selectedCategory}
                onChange={handleCategoryChange}
                fullWidth
                required
              >
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                label="Document Type"
                value={selectedDocType}
                onChange={handleDocTypeChange}
                fullWidth
                required
                disabled={!selectedCategory}
              >
                {allDocumentTypes.map((dt) => (
                  <MenuItem
                    key={dt.id}
                    value={dt.id}
                    sx={{
                      bgcolor: dt.isMandatoryForCategory
                        ? 'error.50'
                        : dt.isIndustrySpecific
                          ? 'warning.50'
                          : 'transparent'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <Typography sx={{ flexGrow: 1 }}>{dt.name}</Typography>
                      {dt.isMandatoryForCategory && (
                        <Chip label="REQUIRED" size="small" color="error" sx={{ height: 20, fontSize: '0.65rem' }} />
                      )}
                      {dt.isOptionalForCategory && (
                        <Chip label="INDUSTRY" size="small" color="warning" sx={{ height: 20, fontSize: '0.65rem' }} />
                      )}
                      {dt.isIndustrySpecific && !dt.isMandatoryForCategory && !dt.isOptionalForCategory && (
                        <Chip label="INDUSTRY" size="small" variant="outlined" color="info" sx={{ height: 20, fontSize: '0.65rem' }} />
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {selectedDocTypeObj && (
              <Grid item xs={12}>
                <Alert
                  severity={selectedDocTypeObj.isMandatoryForCategory ? "warning" : "info"}
                  sx={{ mt: 1 }}
                >
                  {selectedDocTypeObj.isMandatoryForCategory && (
                    <Typography variant="body2" fontWeight="medium" sx={{ mb: 0.5 }}>
                      This document is REQUIRED for {businessCategory?.name || 'your business category'}
                    </Typography>
                  )}
                  <Typography variant="body2">
                    {selectedDocTypeObj.hasExpiry
                      ? `This document type requires an expiry date.`
                      : `This document type does not expire.`}
                  </Typography>
                  {selectedDocTypeObj.allowedFileTypes && (
                    <Typography variant="caption">
                      Accepted formats: {selectedDocTypeObj.allowedFileTypes.join(', ')}
                    </Typography>
                  )}
                </Alert>
              </Grid>
            )}
          </Grid>
        );

      case 1:
        return (
          <Box>
            <FileUploadZone
              files={files}
              onFilesSelect={handleFilesSelect}
              onRemoveFile={handleRemoveFile}
            />
            <ExtractionStatus
              status={extractionStatus}
              progress={extractionProgress}
              extractionResult={extractionResult}
            />
          </Box>
        );

      case 2:
        return (
          <Grid container spacing={2}>
            {/* Authority */}
            {authorities.length > 0 && (
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Issuing Authority"
                  value={selectedAuthority}
                  onChange={(e) => setSelectedAuthority(e.target.value)}
                  fullWidth
                  size="small"
                >
                  {authorities.map((auth) => (
                    <MenuItem key={auth.id} value={auth.id}>
                      {auth.name} ({auth.shortName})
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}

            {/* Document Number */}
            <Grid item xs={12} sm={6}>
              <TextField
                label={selectedDocTypeObj?.documentNumberLabel || 'Document Number'}
                value={documentNumber}
                onChange={(e) => setDocumentNumber(e.target.value)}
                fullWidth
                size="small"
                placeholder={selectedDocTypeObj?.documentNumberPattern ? `Pattern: ${selectedDocTypeObj.documentNumberPattern}` : ''}
              />
            </Grid>

            {/* Issue Date */}
            <Grid item xs={12} sm={6}>
              <TextField
                type="date"
                label="Issue Date"
                value={issueDate}
                onChange={(e) => setIssueDate(e.target.value)}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Expiry Date */}
            {selectedDocTypeObj?.hasExpiry && (
              <Grid item xs={12} sm={6}>
                <TextField
                  type="date"
                  label="Expiry Date"
                  value={expiryDate}
                  onChange={(e) => setExpiryDate(e.target.value)}
                  fullWidth
                  required
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            )}

            {/* Dynamic Fields */}
            {fields.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1, mt: 1 }}>
                  Document Details
                </Typography>
              </Grid>
            )}
            {fields.map((field) => (
              <Grid item xs={12} sm={6} key={field.id}>
                <DynamicFieldRenderer
                  field={field}
                  value={fieldValues[field.fieldCode] || ''}
                  onChange={handleFieldChange}
                  extractedData={extractionResult}
                />
              </Grid>
            ))}

            {/* Notes */}
            <Grid item xs={12}>
              <TextField
                label="Notes (Optional)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                fullWidth
                multiline
                rows={2}
                size="small"
              />
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Add Organization Document
      </DialogTitle>
      <DialogContent dividers>
        {/* Stepper */}
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Step Content */}
        {renderStepContent(activeStep)}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={saving}>
            Back
          </Button>
        )}
        {activeStep < steps.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={!isStepValid(activeStep)}
          >
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!isStepValid(activeStep) || saving}
          >
            {saving ? <CircularProgress size={24} /> : 'Save Document'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AddDocumentDialog;
